/**
 * Register Screen
 * User registration screen
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  StatusBar,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ScrollView,
  Alert,
} from 'react-native';
import Icon from '../components/Icon';
import { authAPI } from '../services/authApi';
import { useTheme } from '../context/ThemeContext';

// Debug mode - set to true to bypass API for testing
const DEBUG_MODE = false;

const RegisterScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const handleRegister = async () => {
    // Validation
    if (!fullName || !email || !password || !confirmPassword) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    // Password match check
    if (password !== confirmPassword) {
      Alert.alert('Error', 'Passwords do not match');
      return;
    }

    // Password strength check
    if (password.length < 6) {
      Alert.alert('Error', 'Password must be at least 6 characters long');
      return;
    }

    // Terms agreement check
    if (!agreeToTerms) {
      Alert.alert('Error', 'Please agree to the Terms & Conditions');
      return;
    }

    setIsLoading(true);

    // Debug mode bypass for testing
    if (DEBUG_MODE) {
      console.log('DEBUG MODE: Bypassing API call');
      Alert.alert(
        'Debug Mode',
        'Registration bypassed for testing. Redirecting to login.',
        [
          {
            text: 'OK',
            onPress: () => navigation.navigate('Login')
          }
        ]
      );
      setIsLoading(false);
      return;
    }

    try {
      // Prepare registration data
      const registrationData = {
        name: fullName.trim(),
        email: email.toLowerCase().trim(),
        password: password,
        phone: phone.trim() || '0000000000',
        refCode: referralCode.trim(),
      };

      console.log('🔵 REGISTRATION START - This will send OTP');
      console.log('📤 Registration payload:', {
        ...registrationData,
        password: '***',
      });

      // Call register API - this will send OTP automatically
      const response = await authAPI.register(registrationData);

      console.log('✅ REGISTRATION API RESPONSE:', response);

      // Check if OTP was sent (your API returns "OTP sent to email. Please verify.")
      // Navigate to verification screen if OTP message is present OR if no token is returned
      const isOTPSent = response?.message && (
        response.message.includes('OTP sent') || 
        response.message.includes('otp') ||
        response.message.includes('verify')
      );
      
      const hasToken = response && response.token;
      
      if (isOTPSent || !hasToken) {
        // Navigate directly to EmailVerification screen
        console.log('📧 OTP sent to email, navigating to verification screen...');
        navigation.navigate('EmailVerification', {
          email: email.toLowerCase().trim(),
          registrationData: registrationData,
          otpAlreadySent: true // Flag to indicate OTP is already sent
        });
      } else {
        // If registration completed directly with token
        if (hasToken) {
          await authAPI.saveToken(response.token);
          Alert.alert(
            'Success',
            'Registration successful! Welcome to TradingView.',
            [
              {
                text: 'OK',
                onPress: () => navigation.replace('MainTabs')
              }
            ]
          );
        } else {
          Alert.alert(
            'Success',
            'Registration successful! Please login with your credentials.',
            [
              {
                text: 'OK',
                onPress: () => navigation.navigate('Login')
              }
            ]
          );
        }
      }
    } catch (error) {
      console.error('Registration error details:', error);
      
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 400) {
          if (data?.message) {
            errorMessage = data.message;
          } else {
            errorMessage = 'Invalid registration data. Please check all fields.';
          }
        } else if (status === 409) {
          errorMessage = 'Email or phone number already exists.';
        }
      }
      
      Alert.alert('Registration Failed', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />
      
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.background }]}>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Fizmo FX Markets Ltd.</Text>
      </View>

      <ScrollView 
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}>
        
        {/* Sign Up Title */}
        <View style={[styles.signupSection, { borderBottomColor: colors.border }]}>
          <Text style={[styles.signupTitle, { color: colors.text }]}>Create a new account</Text>
        </View>

        <View style={styles.formContainer}>
          {/* Full Name Input */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Full Name"
              placeholderTextColor={colors.textTertiary}
              value={fullName}
              onChangeText={setFullName}
              autoCapitalize="words"
            />
          </View>

          {/* Email Input */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Email"
              placeholderTextColor={colors.textTertiary}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          {/* Phone Input */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Phone Number"
              placeholderTextColor={colors.textTertiary}
              value={phone}
              onChangeText={setPhone}
              keyboardType="phone-pad"
              autoCapitalize="none"
            />
          </View>

          {/* Referral Code Input */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Referral Code (Optional)"
              placeholderTextColor={colors.textTertiary}
              value={referralCode}
              onChangeText={(text) => {
                // Only allow integers/numbers
                const numericValue = text.replace(/[^0-9]/g, '');
                setReferralCode(numericValue);
              }}
              keyboardType="numeric"
            />
          </View>

          {/* Password Input */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Password"
              placeholderTextColor={colors.textTertiary}
              secureTextEntry={!showPassword}
              value={password}
              onChangeText={setPassword}
            />
          </View>

          {/* Confirm Password Input */}
          <View style={styles.inputWrapper}>
            <TextInput
              style={[styles.input, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Confirm Password"
              placeholderTextColor={colors.textTertiary}
              secureTextEntry={!showConfirmPassword}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
            />
          </View>

          {/* Terms and Conditions */}
          <TouchableOpacity 
            style={styles.agreeContainer}
            onPress={() => setAgreeToTerms(!agreeToTerms)}>
            <Text style={[styles.agreeText, { color: colors.text }]}>I agree to Terms & Conditions</Text>
            <View style={[styles.checkbox, { borderColor: colors.primary }, agreeToTerms && { backgroundColor: colors.primary }]}>
              {agreeToTerms && <Icon name="checkmark" size={16} color="#FFFFFF" />}
            </View>
          </TouchableOpacity>

          {/* Register Button */}
          <TouchableOpacity
            style={[styles.registerButton, { backgroundColor: colors.primary }, isLoading && { opacity: 0.6 }]}
            onPress={handleRegister}
            disabled={isLoading}>
            <Text style={[styles.registerButtonText, { color: '#FFFFFF' }]}>
              {isLoading ? 'CREATING ACCOUNT...' : 'REGISTER'}
            </Text>
          </TouchableOpacity>

          {/* Already have account */}
          <TouchableOpacity
            style={styles.loginLinkContainer}
            onPress={() => navigation.goBack()}>
            <Text style={[styles.loginLinkText, { color: colors.primary }]}>Already have an account? Sign In</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // backgroundColor will be set dynamically via theme
  },

  header: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingTop: 45,
    // backgroundColor will be set dynamically via theme
  },

  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },

  scrollView: {
    flex: 1,
  },

  signupSection: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },

  signupTitle: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },

  formContainer: {
    paddingHorizontal: 16,
    paddingTop: 24,
  },

  inputWrapper: {
    marginBottom: 16,
  },

  input: {
    height: 48,
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    fontSize: 16,
    paddingHorizontal: 0,
  },

  agreeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    marginBottom: 8,
  },

  agreeText: {
    fontSize: 16,
  },

  checkbox: {
    width: 24,
    height: 24,
    borderWidth: 2,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },

  registerButton: {
    height: 52,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 0,
  },

  registerButtonText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 0.5,
  },

  loginLinkContainer: {
    alignItems: 'center',
    paddingVertical: 12,
    marginTop: 8,
  },

  loginLinkText: {
    fontSize: 15,
    fontWeight: '500',
  },
});

export default RegisterScreen;
